import { reactive } from 'vue'
import axios, { type AxiosProgressEvent } from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import type { AuthState } from '@/types/authState'
import type {
  TaxInjectorSummary,
  TaxInjectorListResponse,
  TaxInjectorRecordsResponse,
  TaxInjectorUploadResponse,
  DownloadUrlResource,
} from '@/types/taxInjector'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COMPLETE = 'COMPLETE',
}

interface TaxInjectorApi {
  state: API_STATES
  error: ApiError | null
  getAll(authState: AuthState, allUsers?: boolean, email?: string, page?: number, size?: number, sort?: string): Promise<TaxInjectorListResponse>
  getRecords(
    authState: AuthState,
    externalId: string,
  ): Promise<TaxInjectorRecordsResponse>
  uploadFile(
    authState: AuthState,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<TaxInjectorUploadResponse>
  getDownloadUrl(
    authState: AuthState,
    externalId: string,
  ): Promise<DownloadUrlResource>
  getById(authState: AuthState, externalId: string): Promise<TaxInjectorSummary>
}

export const taxInjectorApi: TaxInjectorApi = reactive({
  state: API_STATES.INITIAL,
  error: null,

  async getAll(authState: AuthState, allUsers?: boolean, email?: string, page?: number, size?: number, sort?: string): Promise<TaxInjectorListResponse> {
    taxInjectorApi.state = API_STATES.LOADING
    taxInjectorApi.error = null

    const token = authState.user?.accessToken
    const url = API_ENDPOINTS.TAX_INJECTOR.GET_ALL

    const params: Record<string, unknown> = {}
    if (allUsers) {
      params.allUsers = true
      if (email) params.email = email
    }
    if (page !== undefined) params.page = page
    if (size !== undefined) params.size = size
    params.sort = sort ?? 'audit.createdOn,desc'

    return axios
      .get<TaxInjectorListResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      })
      .then(({ data }) => {
        taxInjectorApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        taxInjectorApi.state = API_STATES.ERROR
        taxInjectorApi.error = normalizeError(err)
        throw taxInjectorApi.error
      })
  },

  async getRecords(
    authState: AuthState,
    externalId: string,
  ): Promise<TaxInjectorRecordsResponse> {
    taxInjectorApi.state = API_STATES.LOADING
    taxInjectorApi.error = null

    const token = authState.user?.accessToken
    const url = `${API_ENDPOINTS.TAX_INJECTOR.GET_RECORDS}/${externalId}/records`

    return axios
      .get<TaxInjectorRecordsResponse>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        taxInjectorApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        taxInjectorApi.state = API_STATES.ERROR
        taxInjectorApi.error = normalizeError(err)
        throw taxInjectorApi.error
      })
  },

  async uploadFile(
    authState: AuthState,
    file: File,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
  ): Promise<TaxInjectorUploadResponse> {
    taxInjectorApi.state = API_STATES.LOADING
    taxInjectorApi.error = null

    const token = authState.user?.accessToken
    const url = API_ENDPOINTS.TAX_INJECTOR.UPLOAD_FILE

    const formData = new FormData()
    formData.append('file', file)

    return axios
      .post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        onUploadProgress,
      })
      .then(({ data }) => {
        taxInjectorApi.state = API_STATES.COMPLETE
        return data as TaxInjectorUploadResponse
      })
      .catch(err => {
        taxInjectorApi.state = API_STATES.ERROR
        taxInjectorApi.error = normalizeError(err)
        throw taxInjectorApi.error
      })
  },

  async getDownloadUrl(
    authState: AuthState,
    externalId: string,
  ): Promise<DownloadUrlResource> {
    taxInjectorApi.state = API_STATES.LOADING
    taxInjectorApi.error = null

    const token = authState.user?.accessToken
    const url = `${API_ENDPOINTS.TAX_INJECTOR.GET_DOWNLOAD_URL}/${externalId}/download`

    return axios
      .get<DownloadUrlResource>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        taxInjectorApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        taxInjectorApi.state = API_STATES.ERROR
        taxInjectorApi.error = normalizeError(err)
        throw taxInjectorApi.error
      })
  },

  async getById(
    authState: AuthState,
    externalId: string,
  ): Promise<TaxInjectorSummary> {
    taxInjectorApi.state = API_STATES.LOADING
    taxInjectorApi.error = null

    const token = authState.user?.accessToken
    const url = `${API_ENDPOINTS.TAX_INJECTOR.GET_ALL}/${externalId}`

    return axios
      .get<TaxInjectorSummary>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(({ data }) => {
        taxInjectorApi.state = API_STATES.COMPLETE
        return data
      })
      .catch(err => {
        taxInjectorApi.state = API_STATES.ERROR
        taxInjectorApi.error = normalizeError(err)
        throw taxInjectorApi.error
      })
  },
})

